import { Component, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { Badge } from '../../models/badge.model';
import { AmplitudeService } from '../../services/amplitude.service';
import { ActivitiesService } from '../../services/activities.service';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { BadgesService } from '../../services/badges.service';
import { ConfettiService } from '../../services/confetti.service';
import { GroupsService } from '../../services/groups.service';
import { LocalService } from '../../services/local.service';
import { MatchesService } from '../../services/matches.service';
import { PollsService } from '../../services/polls.service';
import { RoundSummariesService } from '../../services/round-summaries.service';
import { ShareCardsService } from '../../services/share-cards.service';
import { SidenavService } from '../../services/sidenav.service';
import { StandingsService } from '../../services/standings.service';
import { UsersService } from '../../services/users.service';
import { BadgeBookModalComponent } from '../shared/badge-book-modal/badge-book-modal.component';
import { MatchDaySummaryModalComponent } from '../shared/match-day-summary-modal/match-day-summary-modal.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
})
export class HomeComponent implements OnInit {
  sidenav: MatSidenav;
  title: string;
  loggedUser: any;
  standings: any;
  user: any;
  currentTimestamp: number;
  firstRoundIniTimestamp: number;
  finalResult: any;
  isMe: boolean = false;
  isAdmin: boolean = false;
  pichiciPlayer: any = null;
  evolutionLabels: string[] = [];
  evolutionSeries: Array<any> = [];
  evolutionGroups: Array<any> = [];
  evolutionGroupSelected: string = 'general';
  badges: Badge[] = [];
  recentUnlockedBadgeIds: string[] = [];
  pendingPredictions = 0;
  pendingRoundTitle = '';
  nextMatches: any[] = [];
  latestMatchDaySummary: any = null;
  latestGroupSummaries: any[] = [];
  userGroups: any[] = [];
  groupPositions: Array<{
    group: any;
    position: number;
    points: number;
  }> = [];
  activities: any[] = [];
  activityLimit = 3;
  activityComments: Record<string, string> = {};
  readonly activityEmojis = ['👏', '🔥', '⚽', '😬', '👎'];

  private userId: string;

  constructor(
    private sidenavService: SidenavService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private usersService: UsersService,
    private matchesService: MatchesService,
    private pollsService: PollsService,
    private summariesService: RoundSummariesService,
    private standingsService: StandingsService,
    private router: Router,
    private apiService: ApiService,
    private amplitudeService: AmplitudeService,
    private activitiesService: ActivitiesService,
    private badgesService: BadgesService,
    private confettiService: ConfettiService,
    private groupsService: GroupsService,
    private localService: LocalService,
    private bottomSheet: MatBottomSheet,
    private shareCards: ShareCardsService,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
    this.currentTimestamp = Math.floor(Date.now() / 1000);
    this.loggedUser = this.authService.getUser();
    this.isAdmin = this.authService.isAdmin();
    this.title = this.userId ? this.user.name || this.user.email : 'Inicio';
    this.standings = {
      pos: '--',
      points: '--',
      matchesOk: '--',
      sniper: '--',
    };
  }

  async ngOnInit() {
    if (!this.loggedUser?._id) {
      this.router.navigate(['/login']);
      return;
    }

    this.userId = this.route.snapshot.params['id'];

    if (!this.userId) {
      this.userId = this.loggedUser._id;
    }

    this.user = await this.usersService.get(this.userId);
    const groups = await this.groupsService.getList();
    this.userGroups = groups.filter((group: any) =>
      this.user.groups?.includes(group._id),
    );
    this.evolutionGroups = this.userGroups;

    this.isMe = this.loggedUser._id === this.user._id;
    if (this.isMe) {
      await this.authService.updateCurrentUser(this.user);
      this.loggedUser = this.user;
    }
    this.title = this.user?.name || this.user?.email || 'Inicio';
    // this.final_result = this.standingsProv.get_final_result();
    let rounds = await this.matchesService.getRounds();
    this.firstRoundIniTimestamp = rounds[0].iniTimestamp;
    this.standings = await this.standingsService.getUser(this.userId);
    await this.loadBadges();
    const shouldOpenBadgeBook =
      this.route.snapshot.queryParams['openBadgeBook'] === '1';
    await this.loadRecentUnlockedBadges();
    await this.loadEvolutionChart();
    await this.loadEngagementDashboard(rounds);

    if (shouldOpenBadgeBook) {
      this.openBadgeBook();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openBadgeBook: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    if (
      this.isMe &&
      Number(this.standings?.pos) > 0 &&
      Number(this.standings?.pos) <= 3
    ) {
      this.confettiService.launchCelebration();
      this.amplitudeService.logEvent('Top3CelebrationShown', {
        pos: Number(this.standings?.pos),
      });
    }

    this.amplitudeService.logEvent('BadgesViewed', {
      count: this.badges.filter((badge) => badge.unlocked).length,
    });

    const pichichiId = this.getPichichiId(this.user.pichichi);
    if (pichichiId) {
      try {
        this.pichiciPlayer = await this.apiService.request(
          `/players/${pichichiId}`,
          'GET',
        );
      } catch {
        this.pichiciPlayer = null;
      }
    }

    if (!this.isMe) {
      this.amplitudeService.logEvent('HomeVisited', { user: this.user.email });
    }
  }

  toggleMenu() {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate(['/standings']);
  }

  showRules() {
    this.router.navigate([`/rules`]);
  }

  showPoll() {
    this.router.navigate([`/poll/${this.user._id}`]);
  }

  showChampion() {
    this.router.navigate(['/champion']);
  }

  showStandings() {
    this.router.navigate(['/standings']);
  }

  showGroup(groupId: string): void {
    this.router.navigate(['/groups', groupId]);
  }

  shareMatchDay(summary: any): void {
    if (summary) {
      void this.shareCards.shareMatchDay(summary);
    }
  }

  openMatchDaySummary(summary: any): void {
    this.bottomSheet.open(MatchDaySummaryModalComponent, {
      data: summary,
      panelClass: 'match-day-summary-sheet',
    });
  }

  shareBadge(badge: Badge): void {
    void this.shareCards.shareBadge(
      badge,
      this.user?.name || this.user?.email,
    );
  }

  shareGroupPosition(item: {
    group: any;
    position: number;
    points: number;
  }): void {
    void this.shareCards.shareGroupPosition({
      groupName: item.group.name,
      userName: this.user?.name || this.user?.email,
      position: item.position,
      points: item.points,
    });
  }

  async reactToActivity(activity: any, emoji: string): Promise<void> {
    const updated: any = await this.activitiesService
      .react(activity._id, emoji)
      .catch(() => null);
    if (updated) {
      activity.reactions = updated.reactions ?? [];
    }
  }

  async archiveActivity(activity: any): Promise<void> {
    const archived = await this.activitiesService
      .archive(activity._id)
      .catch(() => null);
    if (!archived?.success) return;

    this.activities = this.activities.filter(
      (item) => item._id !== activity._id,
    );
    delete this.activityComments[activity._id];
    if (this.activityLimit !== 3) {
      this.activityLimit = this.activities.length;
    }
  }

  async commentOnActivity(activity: any): Promise<void> {
    const message = (this.activityComments[activity._id] ?? '').trim();
    if (!message) return;
    const updated: any = await this.activitiesService
      .comment(activity._id, message)
      .catch(() => null);
    if (updated) {
      activity.comments = updated.comments ?? [];
      this.activityComments[activity._id] = '';
    }
  }

  reactionCount(activity: any, emoji: string): number {
    return (activity.reactions ?? []).filter(
      (reaction: any) => reaction.emoji === emoji,
    ).length;
  }

  get visibleActivities(): any[] {
    return this.activities.slice(0, this.activityLimit);
  }

  toggleActivities(): void {
    this.activityLimit = this.activityLimit === 3 ? this.activities.length : 3;
  }

  openBadgeBook(): void {
    this.bottomSheet.open(BadgeBookModalComponent, {
      data: {
        badges: this.badges,
        userName: this.user?.name || this.user?.email,
      },
      panelClass: 'badge-book-sheet',
    });
  }

  async selectEvolutionGroup(groupId: string): Promise<void> {
    if (this.evolutionGroupSelected === groupId) {
      return;
    }

    this.evolutionGroupSelected = groupId;
    await this.loadEvolutionChart();
  }

  get unlockedBadgesCount(): number {
    return this.badges.filter((badge) => badge.unlocked).length;
  }

  get homeBadges(): Badge[] {
    const tierOrder: Record<Badge['tier'], number> = {
      gold: 0,
      silver: 1,
      bronze: 2,
    };

    return [...this.badges].sort((a, b) => {
      if (a.unlocked !== b.unlocked) {
        return a.unlocked ? -1 : 1;
      }

      const tierDiff = tierOrder[a.tier] - tierOrder[b.tier];
      if (tierDiff !== 0) {
        return tierDiff;
      }

      return a.title.localeCompare(b.title);
    });
  }

  get nextBadge(): Badge | undefined {
    return this.badges
      .filter((badge) => !badge.unlocked && badge.progress)
      .sort(
        (a, b) =>
          (b.progress?.percentage ?? 0) - (a.progress?.percentage ?? 0),
      )[0];
  }

  isRecentlyUnlocked(id: string): boolean {
    return this.recentUnlockedBadgeIds.includes(id);
  }

  private async loadRecentUnlockedBadges(): Promise<void> {
    const storageKey = `badges-recently-unlocked-${this.userId}`;
    this.recentUnlockedBadgeIds =
      (await this.localService.getDataV2<string[]>(storageKey)) || [];

    if (this.recentUnlockedBadgeIds.length === 0) {
      return;
    }

    this.amplitudeService.logEvent('BadgeUnlockAnimationShown', {
      count: this.recentUnlockedBadgeIds.length,
    });

    setTimeout(() => {
      this.recentUnlockedBadgeIds = [];
      this.localService.removeData(storageKey);
    }, 7000);
  }

  private async loadBadges(): Promise<void> {
    const [catalogResponse, userBadgesResponse] = await Promise.all([
      this.badgesService.getCatalog(),
      this.badgesService.getUserBadgesByUserId(this.userId),
    ]);

    const catalog =
      this.badgesService.normalizeCatalogResponse(catalogResponse);
    this.badges = this.badgesService.normalizeUserBadgesResponse(
      userBadgesResponse,
      catalog,
    );
  }

  private async loadEvolutionChart() {
    const groupId =
      this.evolutionGroupSelected === 'general'
        ? undefined
        : this.evolutionGroupSelected;
    const evolutionData = await this.standingsService.getEvolution(groupId);
    const evolution = evolutionData?.standingsEvolution || [];

    if (!evolution.length) {
      this.evolutionLabels = [];
      this.evolutionSeries = [];
      return;
    }

    const data = evolution.map((journey: any[]) => {
      const pos = journey.findIndex(
        (item: any) => item.user._id === this.userId,
      );
      return pos === -1 ? null : pos + 1;
    });

    this.evolutionLabels = evolution.map(
      (_: any, index: number) => `J${index + 1}`,
    );
    this.evolutionSeries = [
      {
        label: this.user?.name || this.user?.email || 'Jugador',
        data,
        highlight: true,
      },
    ];
  }

  private async loadEngagementDashboard(rounds: any[]): Promise<void> {
    const [matches, poll, latestSummary, activities] = await Promise.all([
      this.matchesService.getList().catch(() => []),
      this.pollsService.get(this.userId).catch(() => null),
      this.summariesService.getLatestMatchDay().catch(() => null),
      this.activitiesService.getFeed().catch(() => []),
    ]);
    this.activities = activities;

    const now = Date.now() / 1000;
    const upcomingRound = rounds.find(
      (round: any) => Number(round.iniTimestamp) > now,
    );
    if (upcomingRound) {
      const roundMatches = matches.filter(
        (match: any) =>
          match.round === upcomingRound.name && match.homeTeam && match.awayTeam,
      );
      const savedMatchIds = new Set(
        (poll?.results ?? [])
          .filter(
            (result: any) =>
              Number.isInteger(result.homeGoals) &&
              Number.isInteger(result.awayGoals),
          )
          .map((result: any) => result.match?._id ?? result.match),
      );
      this.pendingPredictions = roundMatches.filter(
        (match: any) => !savedMatchIds.has(match._id),
      ).length;
      this.pendingRoundTitle = upcomingRound.title;
    }

    this.nextMatches = [...matches]
      .filter((match: any) => new Date(match.date).getTime() > Date.now())
      .sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime(),
      )
      .slice(0, 3);

    this.latestMatchDaySummary =
      latestSummary?.generalSummary ??
      (latestSummary?.status === 'not_generated' ? null : latestSummary);
    this.latestGroupSummaries = latestSummary?.groupSummaries ?? [];

    this.groupPositions = (
      await Promise.all(
        this.userGroups.map(async (group) => {
          const groupStandings: any = await this.standingsService
            .getGroup(group._id)
            .catch(() => null);
          const entries = groupStandings?.currentStandings ?? [];
          const index = entries.findIndex(
            (entry: any) => entry.user?._id === this.userId,
          );
          return index >= 0
            ? {
                group,
                position: index + 1,
                points: entries[index].points ?? 0,
              }
            : null;
        }),
      )
    ).filter((item): item is any => !!item);
  }

  private getPichichiId(pichichi: any): string | null {
    if (!pichichi) {
      return null;
    }

    if (typeof pichichi === 'string') {
      return pichichi;
    }

    return pichichi._id ?? null;
  }
}
